import { Languages } from '@wicadu/langscope'

import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Cómo crear una compra manual',
    seo_description:
      'Descubre cómo crear compras manuales en wicadu para gestionar inventario, actualizar stock y optimizar costos paso a paso.',
    seo_keywords:
      'compras manuales, wicadu, guía wicadu, gestión de inventario, seguimiento de stock, disponibilidad de productos, costo de compra, gestión de costos, métricas financieras, tutorial wicadu, cómo crear compras manuales',
    page_id: PagesId.CreateManualStoreOrders,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      create: 'Cómo crear una compra manual',
    },
    article: {
      title: 'Cómo crear una compra manual',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para configurar <em>opciones de pago</em>, <em>métodos de entrega</em> y <em>gestión de la tienda</em> dentro de <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Stores Dashboard</a>.`,
        '<strong>Compradores</strong> o <strong>Vendedores</strong> con permisos para gestionar <em>inventario</em> y realizar <em>compras manuales</em> o <em>ventas</em>',
      ],
      about_create_orders: {
        title: 'Acerca de crear compras manuales',
        content_type: ContentType.Paragraph,
        content: [
          'Las <strong>compras manuales</strong> te permiten registrar adquisiciones fuera del sistema automatizado, asegurando que tu <strong>inventario</strong> esté siempre actualizado. Con esta herramienta, puedes gestionar compras de <strong>proveedores locales</strong>, <strong>compras directas</strong> y otros canales, optimizando la disponibilidad de <strong>productos</strong> y mejorando la planificación de tu negocio.',
          'Al crear una <strong>compra manual</strong>, puedes ingresar los <strong>productos</strong> adquiridos, asignar un <strong>monto de compra</strong> y establecer la <strong>cantidad</strong> correspondiente. Esta información es crucial para el cálculo de métricas financieras y la gestión de <strong>costos</strong>.',
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Antes de crear una <strong>compra manual</strong>, verifica que los <strong>productos</strong> estén creados en <strong>wicadu</strong>. No es necesario contar con <strong>stock</strong> previo ni precios de venta asignados, pero sí debes tener una <strong>moneda</strong> configurada, la cual se establece automáticamente al crear tu tienda. Para preparar tu <strong>inventario</strong>, sigue estos pasos:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Asegúrate de haber creado al menos un producto. Si no lo has hecho, revisa esta guía:
                  <a href=${getUrlByPageId(PagesId.CreateItems, Languages.Spanish)} title="Guía para crear un producto en wicadu">Cómo crear un producto</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Estos pasos aseguran que los <strong>productos</strong> estén listos para ser incluidos en tus <strong>compras</strong>.',
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Pasos para crear una compra manual',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Compras: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Accede a la funcionalidad de <em>Compras</em> dentro del asistente de <strong>wicadu</strong>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Crear un nuevo pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en el botón <b>+</b> ubicado en la esquina superior derecha de la página.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Seleccionar los productos: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si aún no has agregado productos, serás redirigido automáticamente a la lista de productos. Si ya tienes productos agregados, haz clic en <strong>Agregar producto</strong> si deseas incluir más productos.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Asignar detalles de la compra: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Especifica el <strong>monto de compra</strong> (costo total) y la <strong>cantidad de productos</strong> comprados para cada ítem seleccionado.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Es importante ser preciso con esta información, ya que se utilizará para generar métricas de <strong>costos</strong>, <strong>ganancias</strong> y otros reportes financieros.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Revisar y crear el pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en <strong>Continuar</strong> para ver un resumen con los detalles de la <strong>compra</strong>. Si todo está correcto, presiona <strong>Crear compra</strong> para finalizar el proceso. Esto cambiará el estado del pedido a <strong>Preparado</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> El <strong>stock</strong> de los <strong>productos</strong> no se actualizará automáticamente hasta que la <strong>compra</strong> sea marcada como <em>Entregada</em>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps_on_orders: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Una vez que el pedido esté en estado "Preparado", puedes proceder con las siguientes acciones:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Adjuntar Documentos Opcionales: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Adjunta documentos como factura, recibo, etiqueta de envío, prueba de entrega y más. Consulta
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.Spanish)} title="Guía para adjuntar documentos a un pedido">Cómo adjuntar documentos a un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancelar un Pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si cometiste un error o necesitas eliminar el registro de una <strong>compra</strong>, puedes cancelarla.
                      Aprende cómo hacerlo en: <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.Spanish)} title="Guía para cancelar un pedido">Cómo cancelar un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Agregar Pagos: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Registra los pagos realizados por el cliente en
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.Spanish)} title="Guía para agregar pagos a un pedido">Cómo agregar pagos a un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Entregar el Pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // 'Marca el pedido como entregado con <a href="https://help.wicadu.com/marcar-entregado" title="Guía para marcar un pedido como entregado">Cómo marcar un pedido como entregado</a>.',
                      'Marca el pedido como entregado',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Revisar el Inventario: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Después de registrar la <strong>compra</strong>, verifica que el <strong>stock</strong> de los <strong>productos</strong> se haya actualizado correctamente.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Registra tu primera venta: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Ahora que sabes cómo crear <strong>compras manuales</strong>, te invitamos a registrar tu primera <strong>venta</strong>. Descubre cómo hacerlo aquí:
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.Spanish)} title="Guia para crear un pedido manual en wicadu">Cómo crear un pedido manual</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Corregir errores en el pedido:']
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si cometiste un error al crear un pedido, puedes procesar una <strong>devolución</strong>, <strong>cambio</strong> o <strong>reembolso</strong>. Aprende cómo hacerlo aquí:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.Spanish)} title="Guia para procesar devoluciones, cambios y reembolsos en wicadu">Cómo procesar una devolución, cambio o reembolso</a>`
                    ]
                  }
                ]
              }
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No puedo añadir un producto:</strong> Asegúrate de que esté creado en el sistema.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El inventario no se actualiza:</strong> Verifica que la <strong>compra</strong> esté marcada como <em>Confirmada</em>.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'How to add a Manual Purchase',
    seo_description:
      'Learn how to create manual purchases in wicadu to manage inventory, track stock, and optimize costs with this step-by-step guide.',
    seo_keywords:
      'manual purchases, wicadu, wicadu guide, inventory management, stock tracking, product availability, purchase cost, cost management, financial metrics, business management, how to create manual purchases',
    page_id: PagesId.CreateManualStoreOrders,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      create: 'How to add a Manual Purchase',
    },
    article: {
      title: 'How to add a Manual Purchase',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to configure <em>payment options</em>, <em>delivery methods</em>, and <em>store operations</em> within <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Stores Dashboard</a>.`,
        '<strong>Buyers</strong> or <strong>Sellers</strong> with permissions to manage <em>inventory</em> and create <em>manual purchases</em> or <em>sales</em>.',
      ],
      about_create_orders: {
        title: 'About creating a Manual Order',
        content_type: ContentType.Paragraph,
        content: [
          '<strong>Manual purchases</strong> allow you to track inventory acquisitions made outside the automated system, ensuring your <strong>inventory</strong> remains up to date. This powerful tool lets you manage purchases from <strong>local suppliers</strong>, <strong>direct purchases</strong>, and other channels, optimizing <strong>product</strong> availability and improving your business planning.',
          'When creating a <strong>manual purchase</strong>, you can input the acquired <strong>products</strong>, assign a <strong>purchase amount</strong>, and specify the corresponding <strong>quantity</strong>. This information is critical for calculating financial metrics and managing <strong>costs</strong>.',
        ],
      },
      before_begin: {
        title: 'Before you begin ',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Before adding a <strong>manual purchase</strong>, ensure that the <strong>products</strong> are created in <strong>wicadu</strong>. It is not necessary to have prior <strong>stock</strong> or assigned sales prices, but you must have a configured <strong>currency</strong>, which is automatically set when you create your store. To prepare your <strong>inventory</strong>, follow this step:`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Make sure you have created at least one product. If you haven not, check out this guide
                  <a href=${getUrlByPageId(PagesId.CreateItems, Languages.English)} title="How to create a product guide">How to create a product</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'These steps ensure your <strong>products</strong> are ready to be included in your <strong>purchases</strong>.',
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Steps to create a manual purchase',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to <strong>Purchases</strong>: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Purchases</em> functionality in <strong>wicadu</strong>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Create a new order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click the <b>+</b> button located in the upper right corner of the page.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the products: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `If you haven't added products yet, you will be automatically directed to the product list. If you have already added products, click <strong>Add Product</strong> to include more items.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Assign purchase details: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Specify the <strong>purchase amount</strong> (total cost) and the <strong>quantity of products</strong> purchased for each selected item.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> It is important to be precise with this information, as it will be used to generate metrics for <strong>cost</strong>, <strong>profit</strong>, and other financial reports.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Review and create the order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <strong>Continue</strong> to review the <strong>order summary</strong>, then select <strong>Create Order</strong> to complete the process. This will update the order status to <strong>Prepared</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> The <strong>stock</strong> of the <strong>products</strong> will not be updated automatically until the <strong>purchase</strong> is marked as <em>Delivered</em>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps_on_orders: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Once the order status is set to "Prepared", you can proceed with the following actions:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Attach Optional Documents: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Upload supporting documents such as invoices, receipts, shipping labels, proof of delivery, and more. See the guide:
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.English)} title="How to Attach Documents to an Order Guide">How to Attach Documents to an Order</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancel an Order: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If you made a mistake or need to remove a <strong>purchase</strong>, you can cancel it.
                      Find out how: <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.English)} title="How to Cancel an Order Guide">How to Cancel an Order</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add Payments: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Record payments made by the customer following this guide:
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.English)}  title="How to Add Payments to an Order Guide">How to Add Payments to an Order</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Mark the Order as Delivered: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // `Update the order status to "Delivered" by following the steps in: <a href="https://help.wicadu.com/marcar-entregado" title="How to Mark an Order as Delivered Guide">How to Mark an Order as Delivered</a>.`,
                      `Update the order status to "Delivered".`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Review Inventory: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'After creating the <strong>purchase</strong>, verify that the <strong>stock</strong> of the <strong>products</strong> has been updated correctly.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add your first Sales: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Now that you know how to create <strong>manual purchases</strong>, we invite you to register your first <strong>sale</strong>. Learn how to do it here:
                      <a href="${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.English)}" title="Create a Manual Order in wicadu">How to add a Manual Sale</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Correct mistakes in an order:']
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If you made a mistake creating an order, you can process a <strong>return</strong>, <strong>exchange</strong>, or <strong>refund</strong>. Learn how to do it here:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.English)} title="Process Returns, Exchanges, and Refunds in wicadu guide">How to process a return, exchange, or refund</a>`
                    ]
                  }
                ]
              }
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Cannot add a product:</strong> Make sure it is created in the system.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Inventory is not updating:</strong> Check that the <strong>purchase</strong> is marked as <em>Confirmed</em>.',
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Créer un achat manuel',
    seo_description:
      'Découvrez comment enregistrer un achat manuel dans wicadu afin de mettre à jour le stock et mieux suivre vos coûts.',
    seo_keywords:
      "achat manuel, commande d'achat, inventaire, stock, coûts, wicadu commandes",
    page_id: PagesId.CreateManualStoreOrders,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      create: 'Créer un achat manuel',
    },
    article: {
      title: 'Créer un achat manuel',
      intended: [
        `<strong>Propriétaires</strong> ou <strong>administrateurs</strong> qui configurent les <em>paiements</em>, la <em>livraison</em> et la <em>gestion de boutique</em> dans le <a href=${process.env.STORES_WEBAPP_HOST}>tableau de bord wicadu Stores</a>.`,
        '<strong>Acheteurs</strong> ou <strong>vendeurs</strong> autorisés à gérer l’<em>inventaire</em> et les <em>achats manuels</em>.',
      ],
      about_create_orders: {
        title: 'À propos des achats manuels',
        content_type: ContentType.Paragraph,
        content: [
          'Les <strong>achats manuels</strong> vous permettent d’enregistrer des approvisionnements réalisés en dehors d’un flux automatisé, tout en gardant votre <strong>inventaire</strong>, votre <strong>stock</strong> et la disponibilité de vos <strong>produits</strong> à jour dans <strong>wicadu</strong>. Cette fonctionnalité est particulièrement utile pour suivre les achats auprès de <strong>fournisseurs locaux</strong>, les <strong>achats directs</strong> et d’autres opérations de réapprovisionnement qui influencent la planification de votre activité.',
          'Lors de la création d’un <strong>achat manuel</strong>, vous pouvez ajouter les <strong>produits</strong> acquis, définir le <strong>montant d’achat</strong>, préciser la <strong>quantité</strong> correspondante et conserver un meilleur contrôle sur vos <strong>coûts</strong>, vos mouvements de <strong>stock</strong> et vos indicateurs financiers. C’est une étape importante pour structurer la <strong>gestion des commandes</strong> et améliorer la visibilité de votre <strong>inventaire</strong> dans votre boutique.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Avant d’ajouter un <strong>achat manuel</strong>, vérifiez que les <strong>produits</strong> existent déjà dans <strong>wicadu</strong>. Il n’est pas nécessaire d’avoir un <strong>stock</strong> préalable ni un prix de vente déjà défini, mais votre boutique doit disposer d’une <strong>devise</strong> configurée, automatiquement définie lors de la création de la boutique. Pour préparer correctement votre <strong>inventaire</strong>, suivez cette étape :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Assurez-vous d’avoir créé au moins un produit. Si ce n’est pas encore fait, consultez ce guide :
                  <a href=${getUrlByPageId(PagesId.CreateItems, Languages.French)} title="Créer un produit dans wicadu">Comment créer un produit</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ces vérifications garantissent que vos <strong>produits</strong> sont prêts à être ajoutés à vos <strong>achats</strong> et à votre <strong>inventaire</strong>.',
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Étapes pour créer un achat manuel',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Accéder au module des achats : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Ouvrez la fonctionnalité <em>Achats</em> dans <strong>wicadu</strong>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Créer une nouvelle commande : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur le bouton <b>+</b> en haut à droite de la page.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Sélectionner les produits : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si aucun produit n’a encore été ajouté, vous serez redirigé vers la liste des produits. Si des produits sont déjà disponibles, cliquez sur <strong>Ajouter un produit</strong> pour en inclure davantage.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ["Renseigner les détails de l'achat : "],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Indiquez le <strong>montant de l’achat</strong> (coût total) et la <strong>quantité de produits</strong> achetés pour chaque article sélectionné.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> ces informations doivent être précises, car elles servent au calcul des <strong>coûts</strong>, des <strong>marges</strong> et d’autres indicateurs financiers.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Vérifier puis créer la commande : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur <strong>Continuer</strong> pour vérifier le résumé de la commande, puis sur <strong>Créer la commande</strong> pour terminer. Le statut passera alors à <strong>Préparée</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> le <strong>stock</strong> des <strong>produits</strong> ne sera pas mis à jour automatiquement tant que l’achat ne sera pas marqué comme <em>Livré</em>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps_on_orders: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: ['Une fois que le statut de la commande est défini sur <em>"Préparée"</em>, vous pouvez poursuivre avec les actions suivantes :'],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Ajouter des documents : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Ajoutez des documents comme une facture, un reçu, une étiquette d’expédition, une preuve de livraison et plus encore. Consultez le guide :
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.French)} title="Comment joindre des documents à une commande dans wicadu">Comment joindre des documents à une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Enregistrer les paiements : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Enregistrez les paiements liés à la commande en suivant ce guide :
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.French)} title="Comment ajouter des paiements à une commande dans wicadu">Comment ajouter des paiements à une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Annuler une commande : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si vous avez commis une erreur ou si vous devez supprimer l’enregistrement d’un <strong>achat</strong>, vous pouvez annuler la commande.
                      Découvrez comment faire ici : <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.French)} title="Comment annuler une commande dans wicadu">Comment annuler une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Marquer la commande comme livrée : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: ['Mettez à jour le statut de la commande comme livrée une fois l’achat complètement reçu.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Vérifier l’inventaire : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: ['Après l’enregistrement de l’achat, contrôlez que le stock des produits a bien été mis à jour.'],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Enregistrer votre première vente : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Maintenant que vous savez créer des <strong>achats manuels</strong>, vous pouvez aussi enregistrer votre première <strong>vente manuelle</strong>. Consultez :
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.French)} title="Comment créer une vente manuelle dans wicadu">Comment ajouter une vente manuelle</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Corriger une erreur de commande : '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si vous avez fait une erreur lors de la création d’une commande, vous pouvez traiter un <strong>retour</strong>, un <strong>échange</strong> ou un <strong>remboursement</strong>. Découvrez comment ici :
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.French)} title="Comment traiter un retour, un échange ou un remboursement dans wicadu">Comment traiter un retour, un échange ou un remboursement</a>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Impossible d’ajouter un produit</strong> : assurez-vous qu’il existe déjà dans le système.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le stock ne se met pas à jour</strong> : vérifiez l’état de la commande et la validation finale.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
